import { t } from "i18next";
import { SkeletonTheme } from "react-loading-skeleton";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import MockSkeletonProducts from "./utils/MockSkeletonProducts";
import ActionDropwdownCell from "../../shared/components/Table/ActionDropwdownCell";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PaginationState } from "@tanstack/react-table";
import { useAppSelector } from "../../shared/hook";
import Pagination from "../../shared/components/Table/Pagination";
import { useTranslation } from "react-i18next";
import { table } from "console";
import { getProductCardByNTIN } from "../../common/api/chequesApi";
import { normalizedLocale } from "../../common/libs/normalizedLocale";
import Cookies from "js-cookie";
import { product_groups } from "../../shared/reference/product_groups";
import CopyButton from "../../shared/Ui/CopyButton/CopyButton";

const getActiveGroupId = (): number | null => {
  const raw = Cookies.get("activeGroup");
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

const getActiveGroupCode = (): string | null => {
  const raw = Cookies.get("activeGroupCode");
  return raw ?? null;
}

function MyCards() {
  const [errorModal, setErrorModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const pageSize = useAppSelector((state) => state.userSettings.pageSize);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  });
  const { t, i18n } = useTranslation();
  const [isOpen1, setIsOpen1] = useState(false);
  const [isOpen2, setIsOpen2] = useState(false);
  const [isOpen3, setIsOpen3] = useState(false);
  const [initialValues, setInitialValues] = useState<any>(null)
  const { ntin, productTypeCode } = useParams();
  const locale = normalizedLocale(i18n.language);
  const activeGroupId = getActiveGroupId();
  const activeGroupCode = getActiveGroupCode();
  const currentGroup: any =
    (activeGroupCode && product_groups.find((pg: any) => pg.code === activeGroupCode))
    || (activeGroupId && product_groups.find((pg: any) => pg.id === activeGroupId))
    || product_groups[0];
  const characteristicsCategory: any = currentGroup?.categories?.find((c: any) => c.key === "characteristics");
  const insertionCategory: any = currentGroup?.categories?.find((c: any) => c.key === "insertion" || c.key === "inserts");
  const showInsertsSection: boolean = Boolean(
    (characteristicsCategory?.fields?.some((f: any) => f.value === "insertion" || f.value === "inserts"))
    || insertionCategory
  );
  const cardCategory: any = currentGroup?.categories?.find((c: any) => c.key === "card");
  const descriptionCategory: any = currentGroup?.categories?.find((c: any) => c.key === "description");

  const getCategoryLabel = (category: any, fallbackKey: string) => {
    if (!category) return t(fallbackKey);
    return locale === 'ru' ? category.labelRu : category.labelKz;
  };

  const getFieldLabel = (category: any, jsonKey: string, fallbackKey: string) => {
    const field = category?.fields?.find((f: any) => f.value === jsonKey);
    if (!field) return t(fallbackKey);
    return locale === 'ru' ? field.labelRu : field.labelKz;
  };

  const readFieldValue = (field: any): any => {
    if (!field) return undefined;
    if (field.valueRu || field.valueKz) {
      const localizedKey = locale === 'ru' ? field.valueRu : field.valueKz;
      if (localizedKey) return initialValues?.[localizedKey];
    }
    if (field.valuePath && typeof field.valuePath === 'string') {
      const parts = field.valuePath.split('.');
      let acc: any = initialValues;
      for (const p of parts) {
        if (acc == null) break;
        const index = Number(p);
        acc = Number.isInteger(index) && String(index) === p ? acc?.[index] : acc?.[p];
      }
      return acc;
    }
    if (field.value) return initialValues?.[field.value];
    return undefined;
  };

  const isTnvedCodeField = (field: any) =>
    typeof field?.valuePath === "string" &&
    field.valuePath.includes("tnved") &&
    field.valuePath.includes("tnvedCode");

  const renderFieldValue = (field: any) => {
    if (isTnvedCodeField(field)) {
      const items = Array.isArray(initialValues?.tnved) ? initialValues.tnved : [];
      if (!items.length) {
        return <p className="font-light">-</p>;
      }
      return (
        <div className="flex flex-wrap gap-2 mt-1">
          {items.map((item: any, index: number) => (
            <span
              key={`${item?.tnvedCode ?? "tnved"}-${index}`}
              title={item?.tnvedLabel || undefined}
              className="inline-flex items-center px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-800 text-sm font-medium border border-emerald-200"
            >
              {item?.tnvedCode || "-"}
            </span>
          ))}
        </div>
      );
    }

    const value = readFieldValue(field);
    if (field?.value === "ntin") {
      return (
        <p className="font-light inline-flex items-center gap-1">
          <span>{value ? value : "-"}</span>
          <CopyButton value={value} />
        </p>
      );
    }
    return <p className="font-light">{value ? value : "-"}</p>;
  };

  useEffect(() => {
    onLoad();
  }, [pagination, location.pathname]);

  async function onLoad() {
    if (!ntin) return;

    try {
      const orderResponse = await getProductCardByNTIN(ntin);
      console.log(orderResponse.data, 'orderResponse');

      setInitialValues(orderResponse.data);
    } catch (error) {
      console.error("Ошибка при загрузке данных:", error);
    }
  }

  return (
    <div className="w-full m-[50px] max-w-[1920px] flex flex-col gap-[20px] font-['Inter']">
      <div className="flex flex-col min-w-0">
        <div className="mt-[20px] relative rounded-xl flex flex-row align-center justify-between">
          <h2 className="my-3 text-xl font-semibold">
            {t("productCard.title")}:{' '}
            {/* {initialValues?.nameRu} */}
            {locale === 'ru' ? initialValues?.nameRu : initialValues?.nameKz}
          </h2>
          <button
            className="bg-bg hover:brightness-75 text-gray-500 font-bold py-2 px-4 rounded"
            type="button"
            onClick={() => {
              const base = location.pathname.includes('/my-cards/') ? 'my-cards' : 'all-cards';
              if (productTypeCode) {
                navigate(`/products-catalog/${base}/${productTypeCode}`);
              } else {
                navigate(`/products-catalog/${base}`);
              }
            }}
          >
            {t("mainButtons.close")}
          </button>
        </div>
      </div>

      <div className="w-full flex flex-col bg-white rounded-3xl p-[30px] gap-[30px]">
        <div className="flex items-center text-base text-gray-600 font-light gap-x-[10px]">
          <img src="/assets/docs.png" className="w-[20px]" />
          <div className="uppercase"> {getCategoryLabel(cardCategory, "productCard.title")}</div>
        </div>

        <div className="flex gap-x-[20px] gap-y-[20px] text-base flex-wrap justify-between after:block after:w-full after:h-[1px] border-b-[2px] pb-[20px]">
          <div className="flex flex-col gap-[30px] justify-between flex-wrap max-w-[calc(67%-20px)] w-full ">
            <div className="flex flex-row justify-between gap-[20px] flex-wrap ">
              {cardCategory?.fields?.map((f: any) => (
                <div key={f.value} className="w-full max-w-[calc(50%-10px)] flex-wrap min-w-[160px]">
                  {locale === 'ru' ? f.labelRu : f.labelKz}
                  <p className="font-light">{readFieldValue(f)}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="text-gray-600 justify-center  flex-1 w-full min-w-[200px] max-w-[calc(33%-10px)] h-[200px] flex flex-wrap">
            <div className="text-gray-600  rounded-[10px] w-[200px] h-[200px] font-semibold ">
              {initialValues?.image?.length ? (
                <img
                  src={initialValues.image[0].imageUrl}
                  alt={initialValues.nameRu}
                  className="w-48 h-48 object-cover"
                />
              ) : null}


            </div>
          </div>
        </div>
        <div className="rounded-md">
          <div className="flex flex-col gap-[20px] ">
            <div
              className=" flex items-center text-base text-gray-600 font-light gap-x-[10px] cursor-pointer pb-[20px] border-b-[2px] "
              onClick={() => setIsOpen1(!isOpen1)}
            >
              <img src={"/assets/docs.png"} className="w-[20px]" alt="icon" />
              <div className="uppercase ">{getCategoryLabel(descriptionCategory, "productCard.productDescription")}</div>
              <div
                className={`ml-auto transform transition-transform duration-300 ${isOpen1 ? "rotate-180" : "rotate-0"}`}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </div>
            </div>
            <div
              className={`transition-max-height duration-500 ease-in-out overflow-hidden ${isOpen1 ? "max-h-screen" : "max-h-0"} `}
            >
              <div className="flex flex-wrap gap-[20px] ">
                {descriptionCategory?.fields?.map((f: any) => (
                  <div key={(f.value ?? f.valueRu ?? f.valueKz ?? f.valuePath) as string} className="w-full min-w-[200px] max-w-[calc(33%-10px)]">
                    <div className="text-gray-600 font-semibold">
                      {locale === 'ru' ? f.labelRu : f.labelKz}
                      {renderFieldValue(f)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="flex items-center text-base text-gray-600 font-light gap-x-[10px] cursor-pointer pb-[20px] border-b-[2px]"
              onClick={() => setIsOpen2(!isOpen2)}
            >
              <img src="/assets/docs.png" className="w-[20px]" alt="icon" />
              <div className="uppercase">{getCategoryLabel(characteristicsCategory, "productCard.productCharacteristics")}</div>
              <div
                className={`ml-auto transform transition-transform duration-300 ${isOpen2 ? "rotate-180" : "rotate-0"}`}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </div>
            </div>
            <div
              className={`transition-max-height duration-500 ease-in-out overflow-hidden ${isOpen2 ? "max-h-screen" : "max-h-0"}`}
            >
              <div className="flex flex-wrap gap-[20px] ">
                {characteristicsCategory?.fields?.filter((f: any) => f.value !== 'inserts' && f.value !== 'insertion').map((f: any) => (
                  <div key={f.value} className="w-full min-w-[200px] max-w-[calc(33%-10px)]">
                    <div className="text-gray-600 font-semibold">
                      {locale === 'ru' ? f.labelRu : f.labelKz}
                      <p className="font-light">{readFieldValue(f) ? readFieldValue(f) : '-'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {showInsertsSection && (initialValues?.insertion?.length || insertionCategory) ? (
              <>
                <div className="flex w-full items-center text-base text-gray-600 font-light gap-x-[10px] cursor-pointer"
                  onClick={() => setIsOpen3(!isOpen3)}
                >
                  <img src="/assets/docs.png" className="w-[20px]" />
                  <div className="uppercase">{insertionCategory ? getCategoryLabel(insertionCategory, "productCard.inserts") : t("productCard.inserts")}</div>
                  <div
                    className={`ml-auto transform transition-transform duration-300 ${isOpen3 ? "rotate-180" : "rotate-0"}`}
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </div>
                </div>
                <div className={`transition-max-height duration-500 ease-in-out overflow-hidden ${isOpen3 ? "max-h-screen" : "max-h-0"}`}>
                  <table className="max-w-full w-full leading-normal">
                    <thead className="border-b-2 border-gray-300">
                      <tr>
                        <th
                          className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                          scope="col"
                        >
                          {t("productCard.insertMaterial")}
                        </th>
                        <th
                          className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                          scope="col"
                        >
                          {t("productCard.insertCount")}
                        </th>
                        <th
                          className="px-5 py-3 text-center items-center text-gray-600 font-semibold bg-white border-b border-gray-200 flex justify-center"
                          scope="col"
                        >
                          {t("productCard.weight")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="font-normal text-center">
                      {initialValues?.insertion?.map((value: any, index: any) => (
                        <tr key={`ins-${index}`}>
                          <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600">
                            {value.insertionMaterial}
                          </td>
                          <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600">
                            {value.insertionCount}
                          </td>
                          <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600">
                            {value.insertionWeight}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}

          </div>
        </div>
      </div>
      <div className="h-[10px] py-5"></div>
    </div>
  );
}

export default MyCards;
